import { json_headers, request } from './common';
import type { RequestOptions, SearchServiceEnvelope } from './common';

type RawAnalyticsSearchStatsRecord = {
  search_id: string;
  destination_type: string | null;
  destination_ids: Array<string>;
  landmark_ids: Array<string>;
  property_ids: Array<string>;
  natural_language_query: string;
  prefix_matched_geometry_names: Array<string>;
  prefix_matched_offer_names: Array<string>;
  start_date: string | null;
  end_date: string | null;
  date_type: string | null;
  duration_type: string | null;
  flexible_months: Array<string>;
  adults: number | null;
  children: number | null;
};

export type AnalyticsSearchStatsParams = {
  startDate: string;
  endDate: string;
  limit: number;
};

export type AnalyticsSearchStatsRequestParams = AnalyticsSearchStatsParams & {
  brand: string;
};

export type AnalyticsSearchStatsRecord = {
  searchId: string;
  destinationType: string | null;
  destinationIds: Array<string>;
  landmarkIds: Array<string>;
  propertyIds: Array<string>;
  naturalLanguageQuery: string;
  prefixMatchedGeometryNames: Array<string>;
  prefixMatchedOfferNames: Array<string>;
  startDate: string | null;
  endDate: string | null;
  dateType: string | null;
  durationType: string | null;
  flexibleMonths: Array<string>;
  adults: number | null;
  children: number | null;
};

export type AnalyticsDateRange = {
  startDate: string;
  endDate: string;
};

type RawAnalyticsSearchCounts = {
  all_searches?: number;
  exact_geo_or_vendor_matches?: number;
  remaining_searches?: number;
  empty_query_searches?: number;
};

export type AnalyticsSearchCounts = {
  allSearches: number;
  exactGeoOrVendorMatches: number;
  remainingSearches: number;
  emptyQuerySearches: number;
};

function searchAdminSearchesStatsPath(): string {
  return `${window.configs.API_HOST}/api/search/admin/v1/searches/stats`;
}

function searchAdminSearchesCountsPath(): string {
  return `${window.configs.API_HOST}/api/search/admin/v1/searches/counts`;
}

function normaliseAnalyticsSearchStatsRecord(record: RawAnalyticsSearchStatsRecord): AnalyticsSearchStatsRecord {
  return {
    searchId: record.search_id,
    destinationType: record.destination_type ?? null,
    destinationIds: record.destination_ids ?? [],
    landmarkIds: record.landmark_ids ?? [],
    propertyIds: record.property_ids ?? [],
    naturalLanguageQuery: record.natural_language_query,
    prefixMatchedGeometryNames: record.prefix_matched_geometry_names ?? [],
    prefixMatchedOfferNames: record.prefix_matched_offer_names ?? [],
    startDate: record.start_date ?? null,
    endDate: record.end_date ?? null,
    dateType: record.date_type ?? null,
    durationType: record.duration_type ?? null,
    flexibleMonths: record.flexible_months ?? [],
    adults: record.adults ?? null,
    children: record.children ?? null,
  };
}

export function normaliseAnalyticsSearchStatsRecords(
  records: Array<RawAnalyticsSearchStatsRecord>,
): Array<AnalyticsSearchStatsRecord> {
  return records.map(normaliseAnalyticsSearchStatsRecord);
}

export function normaliseAnalyticsSearchCounts(counts: RawAnalyticsSearchCounts): AnalyticsSearchCounts {
  return {
    allSearches: counts.all_searches ?? 0,
    exactGeoOrVendorMatches: counts.exact_geo_or_vendor_matches ?? 0,
    remainingSearches: counts.remaining_searches ?? 0,
    emptyQuerySearches: counts.empty_query_searches ?? 0,
  };
}

export async function getAnalyticsSearchCounts(
  params: AnalyticsSearchStatsParams,
  options: RequestOptions = {},
): Promise<AnalyticsSearchCounts> {
  const queryString = new URLSearchParams({
    startDate: params.startDate,
    endDate: params.endDate,
    limit: String(params.limit),
  }).toString();

  const response = (await request(`${searchAdminSearchesCountsPath()}?${queryString}`, {
    method: 'GET',
    headers: json_headers,
    signal: options.signal,
  })) as SearchServiceEnvelope<RawAnalyticsSearchCounts>;

  return normaliseAnalyticsSearchCounts(response.result);
}

export async function getAnalyticsSearchStats(
  params: AnalyticsSearchStatsRequestParams,
  options: RequestOptions = {},
): Promise<Array<AnalyticsSearchStatsRecord>> {
  const queryString = new URLSearchParams({
    startDate: params.startDate,
    endDate: params.endDate,
    limit: String(params.limit),
    brand: params.brand,
  }).toString();

  const response = (await request(`${searchAdminSearchesStatsPath()}?${queryString}`, {
    method: 'GET',
    headers: json_headers,
    signal: options.signal,
  })) as SearchServiceEnvelope<Array<RawAnalyticsSearchStatsRecord>>;

  return normaliseAnalyticsSearchStatsRecords(response.result);
}
